import type { AchImportLayoutModelDto } from '@treasury/api/channel';
import { DomainEntity } from '../shared';

export type UploadFormatType = 'delimited' | 'fixedPosition';

export class AchImportLayout extends DomainEntity<AchImportLayoutModelDto> {
    public createDefault(): this {
        return new AchImportLayout({
            uploadFormat: 'delimited',
        } as AchImportLayoutModelDto) as this;
    }

    public get uploadFormat(): UploadFormatType {
        return (this.dto.uploadFormat as UploadFormatType) ?? 'delimited';
    }

    public set uploadFormat(value: UploadFormatType) {
        this.dto.uploadFormat = value;
    }

    public get delimiter() {
        return this.dto.delimiter;
    }

    // Delimited column positions
    public get nameColumn() {
        return this.dto.nameColumn;
    }

    public set nameColumn(value: number | undefined) {
        this.dto.nameColumn = value;
    }

    public get idNumberColumn() {
        return this.dto.idNumberColumn;
    }

    public set idNumberColumn(value: number | undefined) {
        this.dto.idNumberColumn = value;
    }

    public get routingNumberColumn() {
        return this.dto.routingNumberColumn;
    }

    public set routingNumberColumn(value: number | undefined) {
        this.dto.routingNumberColumn = value;
    }

    public get accountNumberColumn() {
        return this.dto.accountNumberColumn;
    }

    public set accountNumberColumn(value: number | undefined) {
        this.dto.accountNumberColumn = value;
    }

    public get amountColumn() {
        return this.dto.amountColumn;
    }

    public set amountColumn(value: number | undefined) {
        this.dto.amountColumn = value;
    }

    public get transactionCodeColumn() {
        return this.dto.transactionCodeColumn;
    }

    public set transactionCodeColumn(value: number | undefined) {
        this.dto.transactionCodeColumn = value;
    }

    public get accountTypeColumn() {
        return this.dto.accountTypeColumn;
    }

    public set accountTypeColumn(value: number | undefined) {
        this.dto.accountTypeColumn = value;
    }

    public get transactionTypeColumn() {
        return this.dto.transactionTypeColumn;
    }

    public set transactionTypeColumn(value: number | undefined) {
        this.dto.transactionTypeColumn = value;
    }

    // Delimited account/transaction type text values
    public get accountTypeCheckingDelimited() {
        return this.dto.accountTypeCheckingDelimited;
    }

    public set accountTypeCheckingDelimited(value: string | undefined) {
        this.dto.accountTypeCheckingDelimited = value;
    }

    public get accountTypeSavingsDelimited() {
        return this.dto.accountTypeSavingsDelimited;
    }

    public set accountTypeSavingsDelimited(value: string | undefined) {
        this.dto.accountTypeSavingsDelimited = value;
    }

    public get accountTypeLoanDelimited() {
        return this.dto.accountTypeLoanDelimited;
    }

    public set accountTypeLoanDelimited(value: string | undefined) {
        this.dto.accountTypeLoanDelimited = value;
    }

    public get accountTypeGlDelimited() {
        return this.dto.accountTypeGlDelimited;
    }

    public set accountTypeGlDelimited(value: string | undefined) {
        this.dto.accountTypeGlDelimited = value;
    }

    public get transactionTypeDebitDelimited() {
        return this.dto.transactionTypeDebitDelimited;
    }

    public set transactionTypeDebitDelimited(value: string | undefined) {
        this.dto.transactionTypeDebitDelimited = value;
    }

    public get transactionTypeCreditDelimited() {
        return this.dto.transactionTypeCreditDelimited;
    }

    public set transactionTypeCreditDelimited(value: string | undefined) {
        this.dto.transactionTypeCreditDelimited = value;
    }

    // Fixed position begin/end pairs
    public get nameBegin() {
        return this.dto.nameBegin;
    }

    public set nameBegin(value: number | undefined) {
        this.dto.nameBegin = value;
    }

    public get nameEnd() {
        return this.dto.nameEnd;
    }

    public set nameEnd(value: number | undefined) {
        this.dto.nameEnd = value;
    }

    public get idNumberBegin() {
        return this.dto.idNumberBegin;
    }

    public set idNumberBegin(value: number | undefined) {
        this.dto.idNumberBegin = value;
    }

    public get idNumberEnd() {
        return this.dto.idNumberEnd;
    }

    public set idNumberEnd(value: number | undefined) {
        this.dto.idNumberEnd = value;
    }

    public get routingNumberBegin() {
        return this.dto.routingNumberBegin;
    }

    public set routingNumberBegin(value: number | undefined) {
        this.dto.routingNumberBegin = value;
    }

    public get routingNumberEnd() {
        return this.dto.routingNumberEnd;
    }

    public set routingNumberEnd(value: number | undefined) {
        this.dto.routingNumberEnd = value;
    }

    public get accountNumberBegin() {
        return this.dto.accountNumberBegin;
    }

    public set accountNumberBegin(value: number | undefined) {
        this.dto.accountNumberBegin = value;
    }

    public get accountNumberEnd() {
        return this.dto.accountNumberEnd;
    }

    public set accountNumberEnd(value: number | undefined) {
        this.dto.accountNumberEnd = value;
    }

    public get amountBegin() {
        return this.dto.amountBegin;
    }

    public set amountBegin(value: number | undefined) {
        this.dto.amountBegin = value;
    }

    public get amountEnd() {
        return this.dto.amountEnd;
    }

    public set amountEnd(value: number | undefined) {
        this.dto.amountEnd = value;
    }

    public get transactionCodeBegin() {
        return this.dto.transactionCodeBegin;
    }

    public set transactionCodeBegin(value: number | undefined) {
        this.dto.transactionCodeBegin = value;
    }

    public get transactionCodeEnd() {
        return this.dto.transactionCodeEnd;
    }

    public set transactionCodeEnd(value: number | undefined) {
        this.dto.transactionCodeEnd = value;
    }

    public get accountTypeBegin() {
        return this.dto.accountTypeBegin;
    }

    public set accountTypeBegin(value: number | undefined) {
        this.dto.accountTypeBegin = value;
    }

    public get accountTypeEnd() {
        return this.dto.accountTypeEnd;
    }

    public set accountTypeEnd(value: number | undefined) {
        this.dto.accountTypeEnd = value;
    }

    public get transactionTypeBegin() {
        return this.dto.transactionTypeBegin;
    }

    public set transactionTypeBegin(value: number | undefined) {
        this.dto.transactionTypeBegin = value;
    }

    public get transactionTypeEnd() {
        return this.dto.transactionTypeEnd;
    }

    public set transactionTypeEnd(value: number | undefined) {
        this.dto.transactionTypeEnd = value;
    }

    // Fixed position account/transaction type text values
    public get accountTypeCheckingFixedPosition() {
        return this.dto.accountTypeCheckingFixedPosition;
    }

    public set accountTypeCheckingFixedPosition(value: string | undefined) {
        this.dto.accountTypeCheckingFixedPosition = value;
    }

    public get accountTypeSavingsFixedPosition() {
        return this.dto.accountTypeSavingsFixedPosition;
    }

    public set accountTypeSavingsFixedPosition(value: string | undefined) {
        this.dto.accountTypeSavingsFixedPosition = value;
    }

    public get accountTypeLoanFixedPosition() {
        return this.dto.accountTypeLoanFixedPosition;
    }

    public set accountTypeLoanFixedPosition(value: string | undefined) {
        this.dto.accountTypeLoanFixedPosition = value;
    }

    public get accountTypeGlFixedPosition() {
        return this.dto.accountTypeGlFixedPosition;
    }

    public set accountTypeGlFixedPosition(value: string | undefined) {
        this.dto.accountTypeGlFixedPosition = value;
    }

    public get transactionTypeDebitFixedPosition() {
        return this.dto.transactionTypeDebitFixedPosition;
    }

    public set transactionTypeDebitFixedPosition(value: string | undefined) {
        this.dto.transactionTypeDebitFixedPosition = value;
    }

    public get transactionTypeCreditFixedPosition() {
        return this.dto.transactionTypeCreditFixedPosition;
    }

    public set transactionTypeCreditFixedPosition(value: string | undefined) {
        this.dto.transactionTypeCreditFixedPosition = value;
    }

    /**
     * Determines whether the delimited form uses Transaction Code (true)
     * or Account Type + Transaction Type (false).
     */
    public get useTransactionCodeDelimited(): boolean {
        return this.transactionCodeColumn !== undefined;
    }

    /**
     * Determines whether the fixed position form uses Transaction Code (true)
     * or Account Type + Transaction Type (false).
     */
    public get useTransactionCodeFixedPosition(): boolean {
        return this.transactionCodeBegin !== undefined;
    }

    public toSaveDto(): AchImportLayoutModelDto {
        const dto = { ...this.dto };
        if (dto.uploadFormat === 'delimited') {
            dto.delimiter = 'comma';
            if (dto.transactionCodeColumn !== undefined) {
                dto.accountTypeColumn = undefined;
                dto.transactionTypeColumn = undefined;
                dto.accountTypeCheckingDelimited = undefined;
                dto.accountTypeSavingsDelimited = undefined;
                dto.accountTypeLoanDelimited = undefined;
                dto.accountTypeGlDelimited = undefined;
                dto.transactionTypeDebitDelimited = undefined;
                dto.transactionTypeCreditDelimited = undefined;
            } else {
                dto.transactionCodeColumn = undefined;
            }
        } else {
            dto.delimiter = undefined;
            if (dto.transactionCodeBegin !== undefined) {
                dto.accountTypeBegin = undefined;
                dto.accountTypeEnd = undefined;
                dto.transactionTypeBegin = undefined;
                dto.transactionTypeEnd = undefined;
                dto.accountTypeCheckingFixedPosition = undefined;
                dto.accountTypeSavingsFixedPosition = undefined;
                dto.accountTypeLoanFixedPosition = undefined;
                dto.accountTypeGlFixedPosition = undefined;
                dto.transactionTypeDebitFixedPosition = undefined;
                dto.transactionTypeCreditFixedPosition = undefined;
            } else {
                dto.transactionCodeBegin = undefined;
                dto.transactionCodeEnd = undefined;
            }
        }
        return dto;
    }
}
